import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api, ApiClientError } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import { useToast } from '../../components/Toast';
import Modal from '../../components/Modal';
import Icon from '../../components/Icon';
import { Badge, Banner, PageLoading, Select, TextInput } from '../../components/ui';
import {
  ROLE_LABEL, STATUS_LABEL, STATUS_TONE, initialsOf, isAdminRole,
} from './roleMeta';
import { DIAL_CODES, splitMobile } from './dialCodes';
import type { ManagerOption, Role, TeamMember, UserStatus } from '../../types';

interface Props {
  userId: string;
  managers: ManagerOption[];
  onClose: () => void;
  onChanged: () => void;
}

export default function UserDetailDialog({ userId, managers, onClose, onChanged }: Props) {
  const { user: me, can, company } = useAuth();
  const toast = useToast();
  const navigate = useNavigate();
  const isAdmin = can('company_admin', 'platform_admin');

  const [person, setPerson] = useState<TeamMember | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [confirmRemove, setConfirmRemove] = useState(false);
  const [passwordPromptRole, setPasswordPromptRole] = useState<Role | null>(null);
  const [newPassword, setNewPassword] = useState('');
  const closePasswordPrompt = useCallback(() => setPasswordPromptRole(null), []);

  const companyDial = DIAL_CODES.find((c) => c.iso === company?.countryCode)?.dial ?? '+91';

  const [fullName, setFullName] = useState('');
  const [email, setEmail] = useState('');
  const [mobile, setMobile] = useState('');
  const [dialCode, setDialCode] = useState(companyDial);
  const [designation, setDesignation] = useState('');
  const [employeeCode, setEmployeeCode] = useState('');
  const [reportsToId, setReportsToId] = useState('');

  const isSelf = me?.id === userId;

  const load = async () => {
    try {
      const data = await api.get<TeamMember>(`/users/${userId}`);
      setPerson(data);
      setFullName(data.fullName);
      setEmail(data.email ?? '');
      const { dial, local } = splitMobile(data.mobile, companyDial);
      setDialCode(dial);
      setMobile(local);
      setDesignation(data.designation ?? '');
      setEmployeeCode(data.employeeCode ?? '');
      setReportsToId(data.reportsTo?.id ?? '');
    } catch (err) {
      setLoadError(err instanceof ApiClientError ? err.message : 'Could not load them.');
    }
  };

  useEffect(() => { void load(); /* eslint-disable-next-line */ }, [userId]);

  /** Every write goes through here so failures are reported the same way. */
  async function act<T>(fn: () => Promise<T>, success: string) {
    setBusy(true);
    try {
      await fn();
      toast.success(success);
      await load();
      onChanged();
      return true;
    } catch (err) {
      toast.error(err instanceof ApiClientError ? err.message : 'That did not work.');
      return false;
    } finally {
      setBusy(false);
    }
  }

  const combinedMobile = mobile.trim() ? dialCode + mobile.trim() : '';
  const dirty = person !== null && (
    fullName !== person.fullName
    || email !== (person.email ?? '')
    || combinedMobile !== (person.mobile ?? '')
    || designation !== (person.designation ?? '')
    || employeeCode !== (person.employeeCode ?? '')
    || reportsToId !== (person.reportsTo?.id ?? '')
  );

  const save = () => act(
    () => api.patch(`/users/${userId}`, {
      fullName: fullName.trim(),
      email: email.trim(),
      mobile: mobile.trim() ? dialCode + mobile.trim() : '',
      designation,
      employeeCode,
      reportsToId: reportsToId || null,
    }),
    'Saved.',
  );

  const changeRole = (role: Role, password?: string) => act(
    () => api.patch(`/users/${userId}/role`, { role, ...(password ? { password } : {}) }),
    `${person?.fullName} is now ${ROLE_LABEL[role].toLowerCase()}.`,
  );

  const setStatus = (status: 'active' | 'suspended') => act(
    () => api.patch(`/users/${userId}/status`, { status }),
    status === 'suspended'
      ? `${person?.fullName} can no longer sign in.`
      : `${person?.fullName} is active again.`,
  );

  const resend = () => act(
    () => api.post(`/users/${userId}/resend-invite`),
    'Invitation sent again.',
  );

  const remove = async () => {
    const ok = await act(() => api.del(`/users/${userId}`), `${person?.fullName} removed.`);
    if (ok) onClose();
  };

  if (loadError) {
    return (
      <Modal title="Team member" onClose={onClose}>
        <Banner kind="danger">{loadError}</Banner>
      </Modal>
    );
  }

  if (!person) {
    return (
      <Modal title="Team member" onClose={onClose}>
        <PageLoading rows={3} />
      </Modal>
    );
  }

  const status = person.status as UserStatus;
  const notJoined = Boolean(person.pendingInvitationId);
  // A manager may edit their own reports but never an admin.
  const mayEdit = isAdmin || !isAdminRole(person.role);

  return (
    <Modal
      title={person.fullName}
      subtitle={[ROLE_LABEL[person.role], person.designation].filter(Boolean).join(' · ')}
      onClose={onClose}
      wide
      footer={(
        <>
          {isAdmin && !isSelf ? (
            <button
              type="button"
              className="btn btn-danger-ghost"
              disabled={busy}
              onClick={() => setConfirmRemove(true)}
            >
              <Icon name="trash" size={15} /> Remove from team
            </button>
          ) : null}
          <div className="grow" />
          <button type="button" className="btn btn-secondary" onClick={onClose}>Close</button>
          {dirty ? (
            <button type="button" className="btn btn-primary" disabled={busy} onClick={() => void save()}>
              {busy ? <span className="spinner" /> : null} Save changes
            </button>
          ) : null}
        </>
      )}
    >
      <div className="col gap-24">
        <div className="row gap-12">
          <div className="avatar" style={{ width: 44, height: 44, fontSize: 16 }}>
            {initialsOf(person.fullName)}
          </div>
          <div className="grow">
            <div className="row gap-8 wrap">
              <Badge tone={STATUS_TONE[status]}>{STATUS_LABEL[status]}</Badge>
              <Badge tone={isAdminRole(person.role) ? 'brand' : 'neutral'}>
                {ROLE_LABEL[person.role]}
              </Badge>
              {isSelf ? <Badge tone="brand">This is you</Badge> : null}
            </div>
            <div className="small faint" style={{ marginTop: 4 }}>
              {person.mobile ?? '—'}{person.email ? ` · ${person.email}` : ''}
            </div>
            {person.role === 'sales_rep' ? (
              <button type="button" className="btn btn-secondary btn-sm" style={{ marginTop: 8 }}
                onClick={() => { onClose(); navigate(`/reports/rep/${userId}`); }}>
                View performance dashboard
              </button>
            ) : null}
          </div>
        </div>

        {notJoined ? (
          <Banner kind="warning">
            <strong>They have not joined yet.</strong>{' '}
            {isAdminRole(person.role)
              ? 'The invitation email is waiting for them to set a password.'
              : 'They still need to enter their team code in the app.'}
            <div style={{ marginTop: 8 }}>
              <button type="button" className="btn btn-secondary btn-sm" disabled={busy} onClick={() => void resend()}>
                Send the invitation again
              </button>
            </div>
          </Banner>
        ) : null}

        {/* -- details -- */}
        <div className="col gap-16">
          <TextInput
            label="Full name"
            value={fullName}
            maxLength={120}
            disabled={!mayEdit}
            onChange={(e) => setFullName(e.target.value)}
          />
          <div className="row gap-12" style={{ alignItems: 'flex-start' }}>
            <div className="col gap-4" style={{ flex: 1 }}>
              <label style={{ fontSize: 13, fontWeight: 600 }}>Mobile number</label>
              <div className="row gap-4">
                <select
                  value={dialCode}
                  onChange={(e) => setDialCode(e.target.value)}
                  className="input"
                  style={{ width: 120, flexShrink: 0, fontSize: 13 }}
                  disabled={!mayEdit}
                >
                  {DIAL_CODES.map((c) => (
                    <option key={c.iso} value={c.dial}>
                      {c.iso} {c.dial}
                    </option>
                  ))}
                </select>
                <TextInput
                  value={mobile}
                  maxLength={15}
                  disabled={!mayEdit}
                  placeholder="98765 43210"
                  onChange={(e) => setMobile(e.target.value)}
                />
              </div>
              <span className="small faint">How a salesman signs in (mobile + OTP).</span>
            </div>
            <TextInput
              label="Email address"
              type="email"
              value={email}
              maxLength={200}
              disabled={!mayEdit}
              placeholder="name@company.com"
              hint="How an admin signs in (email + password)."
              onChange={(e) => setEmail(e.target.value)}
            />
          </div>
          <div className="row gap-12" style={{ alignItems: 'flex-start' }}>
            <TextInput
              label="Designation"
              value={designation}
              maxLength={80}
              disabled={!mayEdit}
              placeholder="Field Officer"
              onChange={(e) => setDesignation(e.target.value)}
            />
            <TextInput
              label="Employee code"
              value={employeeCode}
              maxLength={40}
              disabled={!mayEdit}
              placeholder="EMP-0142"
              onChange={(e) => setEmployeeCode(e.target.value.toUpperCase())}
            />
          </div>
          <Select
            label="Reports to"
            value={reportsToId}
            disabled={!mayEdit}
            hint={person.directReports
              ? `${person.directReports} ${person.directReports === 1 ? 'person reports' : 'people report'} to them.`
              : undefined}
            onChange={(e) => setReportsToId(e.target.value)}
          >
            <option value="">Nobody</option>
            {managers
              .filter((m) => m.id !== person.id)
              .map((m) => (
                <option key={m.id} value={m.id}>{m.fullName} — {ROLE_LABEL[m.role]}</option>
              ))}
          </Select>
        </div>

        {/* -- role -- */}
        <div className="col gap-8" style={{ borderTop: '1px solid var(--border)', paddingTop: 20 }}>
          <h3>Role</h3>
          {isSelf ? (
            <Banner kind="info">
              You cannot change your own role. Ask another admin to do it — that is what
              stops an account being locked out of its own console.
            </Banner>
          ) : (
            <div className="row gap-8 wrap">
              {(['company_admin', 'manager', 'sales_rep'] as Role[]).map((role) => (
                <button
                  key={role}
                  type="button"
                  data-role-button={role}
                  className={`btn btn-sm ${person.role === role ? 'btn-primary' : 'btn-secondary'}`}
                  disabled={busy || person.role === role || (!isAdmin && isAdminRole(role))}
                  onClick={() => {
                    if (isAdminRole(role) && !person.hasPassword) {
                      setPasswordPromptRole(role);
                      setNewPassword('');
                    } else {
                      void changeRole(role);
                    }
                  }}
                >
                  {ROLE_LABEL[role]}
                </button>
              ))}
            </div>
          )}
        </div>

        {/* -- access -- */}
        {!isSelf && mayEdit ? (
          <div className="col gap-8" style={{ borderTop: '1px solid var(--border)', paddingTop: 20 }}>
            <h3>Access</h3>
            {status === 'suspended' ? (
              <>
                <p className="small muted">
                  They cannot sign in, and any session they had open has already stopped working.
                  Everything they recorded is kept.
                </p>
                <div>
                  <button
                    type="button"
                    className="btn btn-secondary btn-sm"
                    disabled={busy}
                    onClick={() => void setStatus('active')}
                  >
                    <Icon name="check" size={14} /> Let them back in
                  </button>
                </div>
              </>
            ) : (
              <>
                <p className="small muted">
                  Suspending stops them signing in straight away, on whatever device they are
                  holding. Their attendance, customers and place in the team all stay — which
                  is usually what you want when somebody leaves.
                </p>
                <div>
                  <button
                    type="button"
                    className="btn btn-secondary btn-sm"
                    disabled={busy}
                    data-testid="suspend"
                    onClick={() => void setStatus('suspended')}
                  >
                    Suspend
                  </button>
                </div>
              </>
            )}
          </div>
        ) : null}

        <div className="small faint">
          Added {new Date(person.createdAt).toLocaleDateString()}
          {person.lastLoginAt
            ? ` · last signed in ${new Date(person.lastLoginAt).toLocaleString()}`
            : ' · has never signed in'}
        </div>
      </div>

      {passwordPromptRole ? (
        <Modal
          title={`Set password for ${person.fullName}`}
          onClose={closePasswordPrompt}
          dismissable={false}
          footer={(
            <>
              <button type="button" className="btn btn-secondary" onClick={closePasswordPrompt}>
                Cancel
              </button>
              <button
                type="button"
                className="btn btn-primary"
                disabled={busy || newPassword.length < 8}
                onClick={async () => {
                  const ok = await changeRole(passwordPromptRole, newPassword);
                  if (ok) setPasswordPromptRole(null);
                }}
              >
                {busy ? <span className="spinner" /> : null} Set password & change role
              </button>
            </>
          )}
        >
          <div className="col gap-12">
            <Banner kind="info">
              An administrator signs in with email and password.
              Set a password so they can sign in to the web console.
            </Banner>
            <TextInput
              label="New password"
              type="password"
              value={newPassword}
              maxLength={100}
              autoFocus
              placeholder="At least 8 characters"
              onChange={(e) => setNewPassword(e.target.value)}
            />
          </div>
        </Modal>
      ) : null}

      {confirmRemove ? (
        <Modal
          title={`Remove ${person.fullName}?`}
          onClose={() => setConfirmRemove(false)}
          dismissable={false}
          footer={(
            <>
              <button type="button" className="btn btn-secondary" onClick={() => setConfirmRemove(false)}>
                Keep them
              </button>
              <button type="button" className="btn btn-danger" disabled={busy} onClick={() => void remove()}>
                {busy ? <span className="spinner" /> : null} Remove
              </button>
            </>
          )}
        >
          <div className="col gap-12">
            <Banner kind="warning">
              They lose access immediately and their pending invitation stops working.
            </Banner>
            <p className="small muted">
              Nothing they recorded is deleted — their attendance and the customers they
              added stay in your reports, attributed to them.
              {person.directReports
                ? ` Their ${person.directReports} direct ${person.directReports === 1 ? 'report moves' : 'reports move'} up to ${person.reportsTo?.fullName ?? 'nobody'}.`
                : ''}
            </p>
            <p className="small muted">
              If they have simply left, <strong>suspending</strong> is usually the better
              answer — it keeps them in the team list so past reports still read sensibly.
            </p>
          </div>
        </Modal>
      ) : null}
    </Modal>
  );
}
